/**
 * The feed flow's pure half: how much a tier burns, what the primary button may
 * do, and whether my burn made the brain think.
 *
 * No DOM and no provider here - `feed-panel.ts` owns those, and `feed.ts` owns
 * every wallet call. Keeping the three decisions separate is what makes them
 * testable in node (test/feed-flow.test.ts).
 */

import type { Feed, Tick } from "./api";

/** The three burn tiers the brain contract accepts (src/Obrain.sol). */
export type TierId = 0 | 1 | 2;

export const TIER_IDS: readonly TierId[] = [0, 1, 2];

/** What the brain charges for a tier, read from the chain, never remembered. */
export function tierBurnAmount(amounts: readonly [string, string, string], tier: TierId): bigint {
  return BigInt(amounts[tier]);
}

/**
 * What the primary button may do, given the wallet's own numbers.
 * Ordered: a burn the wallet cannot pay for is named before an approval the
 * user would only have to give in order to fail.
 */
export type Readiness =
  | { readonly kind: "no-wallet" }
  | { readonly kind: "insufficient"; readonly short: bigint }
  | { readonly kind: "needs-approval" }
  | { readonly kind: "ready" };

export function feedReadiness(view: {
  readonly balance: bigint | null;
  readonly allowance: bigint | null;
  readonly amount: bigint;
}): Readiness {
  const { balance, allowance, amount } = view;

  if (balance === null || allowance === null) return { kind: "no-wallet" };

  if (balance < amount) return { kind: "insufficient", short: amount - balance };

  if (allowance < amount) return { kind: "needs-approval" };

  return { kind: "ready" };
}

/**
 * The approval the flow grants when the brain cannot move the tokens yet: a
 * standing grant rather than a per-feed amount, so one approval covers every
 * later feed. Safe by construction - the brain contract has no code path that
 * moves a token anywhere except the dead address (src/Obrain.sol feed()).
 */
export function allowanceNeeded(): bigint {
  return 2n ** 200n;
}

/**
 * The panel's notes are markup (the dictionary carries `<b>`), and two of them
 * interpolate a wallet's or a node's own error text - so that text is escaped
 * before it goes in. A provider must never be able to render elements into the
 * page that is asking for its signature.
 */
export function escapeHtml(text: string): string {
  return text.replace(/[&<>]/g, (character) => `&#${character.charCodeAt(0)};`);
}

/** The thought my burn opened: the burn is onchain before its fired set is. */
export type FeedConfirmation = {
  readonly tick: number;
  /** null while the indexer holds the burn but has not decoded the thought */
  readonly fired: number | null;
};

/**
 * My feed, joined against the thought it opened. The indexer decodes the Feed
 * row and the BrainState row separately, so the burn can be visible while the
 * fired set is still missing - the panel says so rather than inventing a count.
 */
export function feedConfirmation(
  feeds: readonly Feed[],
  ticks: readonly Tick[],
  txHash: string,
): FeedConfirmation | null {
  const mine = feeds.find((feed) => feed.txHash.toLowerCase() === txHash.toLowerCase());

  if (mine === undefined) return null;

  const thought = ticks.find((tick) => tick.tick === mine.tick);

  return { tick: mine.tick, fired: thought === undefined ? null : thought.fired.length };
}
